import { Button, CodeEntry, Heading, Modal, Text, useToast } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { FunctionComponent, useContext } from 'react'
import { useFormatMobile, useCodeEntry } from '../../../../hooks'
import { useAmplitudeWithEnduringEventProperties } from '../../../../integrations/amplitude'
import { TrackingEvent } from '../../../../model/amplitude'
import { useChangeMobileCheckMobileMutation, useChangeMobileVerifyMobileMutation } from '../../../../state/account/changeMobile'
import { EditMobileNumberModalContext, EditMobileNumberSteps } from '../../EditMobileNumberModal'

const I18N_NAMESPACE = ['common', 'account']

interface ConfirmProps {
  closeModal: () => void
}

export const Confirm: FunctionComponent<ConfirmProps> = ({ closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { codeEntryRef, codeEntryKey, resetCodeEntry } = useCodeEntry()
  const { token, newMobile, setStep } = useContext(EditMobileNumberModalContext)
  const setToast = useToast()

  const [checkMobile, { isError: isCheckMobileError, isLoading: isCheckMobileLoading }] = useChangeMobileCheckMobileMutation()

  const onCodeEntryComplete = async (code: string): Promise<void> => {
    try {
      await checkMobile({
        body: {
          code,
          token,
        },
      }).unwrap()
      logEvent(TrackingEvent.EDIT_MOBILE_SUCCESS)
      closeModal()
      setToast({
        message: t('account:edit:mobile:confirm:toast:success:message'),
        kind: 'Success',
        manualDismiss: true,
        toastTime: 2000,
        testNameSpace,
      })
    } catch (err) {
      logEvent(TrackingEvent.EDIT_MOBILE_ERROR)
      resetCodeEntry()
    }
  }

  const [verifyMobile] = useChangeMobileVerifyMobileMutation()

  const onResendCode = async (): Promise<void> => {
    try {
      await verifyMobile({
        body: {
          newMobile,
          token,
        },
      }).unwrap()
    } catch (err) {}
  }

  const onGoBack = (): void => setStep(EditMobileNumberSteps.Edit)

  const isCodeEntryDisabled = isCheckMobileLoading

  const testNameSpace = 'edit-mobile-number-modal-confirm'

  return (
    <>
      <Modal.Header divider>
        <div className='text-center'>
          <Heading testNameSpace={testNameSpace} size='L'>
            {t('account:edit:mobile:confirm:header:heading')}
          </Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div>
          <div className='mt-2'>
            <Text>{t('account:edit:mobile:confirm:content:message', { newMobile: useFormatMobile(newMobile) })}</Text>
          </div>
          <div className='mt-4'>
            <CodeEntry
              codeEntryLength={6}
              onComplete={onCodeEntryComplete}
              disabled={isCodeEntryDisabled}
              error={isCheckMobileError}
              errorText={t('account:edit:mobile:confirm:content:code-entry:error')}
              testNameSpace={testNameSpace}
              ref={codeEntryRef}
              key={codeEntryKey}
            />
          </div>
          <div className='mt-6'>
            <Text size='S'>
              <Trans
                i18nKey='account:edit:mobile:confirm:content:resend'
                components={{
                  resendButton: <Button.Link size='Regular' onClick={onResendCode} />,
                  goBackButton: <Button.Link size='Regular' onClick={onGoBack} />,
                }}
              />
            </Text>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
